import { useCallback, useEffect, useState } from "react";
import {
  MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle,
  MDBModalBody, MDBModalFooter, MDBBtn, MDBInput
} from "mdb-react-ui-kit";
import "./SectionEditor.css";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import MfmFilePicker from "Projects/CC_cubi_clan_project/GlobalComponents/MfmFilePicker/MfmFilePicker";
import MediaTrimmer from "Projects/EXHI_exhibiciones_project/Components/MediaTrimmer/MediaTrimmer";

export default function SectionEditor({ show, setShow, idExhibicion, seccion, onSaved, onDeleted }) {
  const isEdit = !!(seccion && seccion.idSeccion);

  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [tiempoInicio, setTiempoInicio] = useState(0);
  const [duracion, setDuracion] = useState(30);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Medios
  const [medioVisual, setMedioVisual] = useState(null);
  const [mediosAudio, setMediosAudio] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState("visual"); // "visual" | "audio"
  const [trimTarget, setTrimTarget] = useState(null);

  useEffect(() => {
    if (show && seccion) {
      setTitulo(seccion.titulo || "");
      setDescripcion(seccion.descripcion || "");
      setTiempoInicio(seccion.tiempoInicio ?? 0);
      setDuracion(seccion.duracion ?? 30);
      setMedioVisual(seccion.medioVisual || null);
      setMediosAudio(seccion.mediosAudio || []);
    } else if (show && !seccion) {
      setTitulo("");
      setDescripcion("");
      setTiempoInicio(0);
      setDuracion(30);
      setMedioVisual(null);
      setMediosAudio([]);
    }
    setConfirmDelete(false);
  }, [show, seccion]);

  async function handleSave() {
    if (!titulo.trim()) {
      toast.show({ message: "El título es obligatorio", type: "warning" });
      return;
    }
    setSaving(true);
    try {
      const body = new URLSearchParams();
      body.append("titulo", titulo.trim());
      body.append("descripcion", descripcion.trim());
      body.append("tiempoInicio", tiempoInicio);
      body.append("duracion", duracion);

      let res;
      if (isEdit) {
        console.log(`[EXHI:SectionEditor] handleSave UPDATE — idSeccion=${seccion.idSeccion} titulo="${titulo}" tiempoInicio=${tiempoInicio} duracion=${duracion}`);
        res = await doFetchJson("EXHI", `secciones/${seccion.idSeccion}`, {
          method: "PUT",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });
      } else {
        console.log(`[EXHI:SectionEditor] handleSave CREATE — idExhibicion=${idExhibicion} titulo="${titulo}" tiempoInicio=${tiempoInicio} duracion=${duracion}`);
        res = await doFetchJson("EXHI", `exhibiciones/${idExhibicion}/secciones`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });
      }
      console.log("[EXHI:SectionEditor] handleSave — respuesta:", res?.STATE, res?.MESSAGE);
      if (res?.STATE === "SUCCESS") {
        toast.show({ message: isEdit ? "Sección actualizada" : "Sección creada", type: "success" });
        onSaved && onSaved(res.DATA);
      } else {
        toast.show({ message: res?.MESSAGE || "Error al guardar", type: "danger" });
      }
    } catch (err) {
      console.error("[EXHI:SectionEditor] handleSave — ERROR:", err);
      toast.show({ message: "Error de conexión", type: "danger" });
    } finally {
      setSaving(false);
    }
  }

  async function handlePickerSelect(file) {
    setPickerOpen(false);
    const seccionId = seccion?.idSeccion;
    console.log(`[EXHI:SectionEditor] handlePickerSelect — modo=${pickerMode} seccionId=${seccionId ?? "nueva"} uuid=${file.uuid} tipo=${file.mediaType}`);
    if (!seccionId) {
      // Sección nueva: guardar temporalmente en estado local
      if (pickerMode === "visual") {
        setMedioVisual({ mfmUuid: file.uuid, mediaType: file.mediaType?.toUpperCase() || "IMAGE", tituloMfm: file.title, thumbUrl: file.thumbUrl });
      } else {
        setMediosAudio((prev) => [...prev, { mfmUuid: file.uuid, mediaType: file.mediaType?.toUpperCase() || "AUDIO", tituloMfm: file.title }]);
      }
      toast.show({ message: "Medio seleccionado. Guarda la sección para persistir.", type: "info" });
      return;
    }

    // Sección existente: persistir en backend
    const bodyParams = new URLSearchParams();
    bodyParams.append("mfmUuid", file.uuid);
    bodyParams.append("mediaType", (file.mediaType || "IMAGE").toUpperCase());

    const endpoint = pickerMode === "visual"
      ? `secciones/${seccionId}/medios/visual`
      : `secciones/${seccionId}/medios/audio`;
    console.log(`[EXHI:SectionEditor] handlePickerSelect — endpoint=${endpoint}`);

    try {
      const res = await doFetchJson("EXHI", endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: bodyParams.toString(),
      });
      console.log("[EXHI:SectionEditor] handlePickerSelect — respuesta:", res?.STATE, res?.MESSAGE);
      if (res?.STATE === "SUCCESS") {
        toast.show({ message: "Medio asignado", type: "success" });
        onSaved && onSaved();
      } else {
        toast.show({ message: res?.MESSAGE || "Error al asignar medio", type: "danger" });
      }
    } catch (err) {
      console.error("[EXHI:SectionEditor] handlePickerSelect — ERROR:", err);
      toast.show({ message: "Error de conexión", type: "danger" });
    }
  }

  async function handleRemoveMedio(idSeccionMedio) {
    if (!idSeccionMedio) return;
    console.log(`[EXHI:SectionEditor] handleRemoveMedio — idSeccionMedio=${idSeccionMedio}`);
    try {
      const res = await doFetchJson("EXHI", `medios/${idSeccionMedio}`, { method: "DELETE" });
      console.log("[EXHI:SectionEditor] handleRemoveMedio — respuesta:", res?.STATE, res?.MESSAGE);
      if (res?.STATE === "SUCCESS") {
        toast.show({ message: "Medio eliminado", type: "success" });
        onSaved && onSaved();
      }
    } catch (err) {
      console.error("[EXHI:SectionEditor] handleRemoveMedio — ERROR:", err);
      toast.show({ message: "Error al eliminar medio", type: "danger" });
    }
  }

  async function handleDeleteSection() {
    console.log(`[EXHI:SectionEditor] handleDeleteSection — idSeccion=${seccion.idSeccion}`);
    setConfirmDelete(false);
    setSaving(true);
    try {
      const res = await doFetchJson("EXHI", `secciones/${seccion.idSeccion}`, { method: "DELETE" });
      console.log("[EXHI:SectionEditor] handleDeleteSection — respuesta:", res?.STATE, res?.MESSAGE);
      if (res?.STATE === "SUCCESS") {
        toast.show({ message: "Sección eliminada", type: "success" });
        setShow(false);
        onDeleted && onDeleted(seccion.idSeccion);
      } else {
        toast.show({ message: res?.MESSAGE || "Error al eliminar", type: "danger" });
      }
    } catch (err) {
      console.error("[EXHI:SectionEditor] handleDeleteSection — ERROR:", err);
      toast.show({ message: "Error de conexión", type: "danger" });
    } finally {
      setSaving(false);
    }
  }

  const handleClose = useCallback(() => {
    setShow(false);
  }, [setShow]);

  return (
    <>
      <MDBModal show={show} onHide={handleClose} tabIndex="-1">
        <MDBModalDialog size="lg">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{isEdit ? "Editar sección" : "Nueva sección"}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={handleClose} />
            </MDBModalHeader>
            <MDBModalBody>
              <div className="row g-3 mb-3">
                <div className="col-12">
                  <MDBInput label="Título" value={titulo} onChange={(e) => setTitulo(e.target.value)} required />
                </div>
                <div className="col-12">
                  <MDBInput label="Descripción (opcional)" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
                </div>
                <div className="col-6">
                  <MDBInput
                    type="number"
                    label="Tiempo inicio (seg)"
                    value={tiempoInicio}
                    min={0}
                    onChange={(e) => setTiempoInicio(Number(e.target.value))}
                  />
                </div>
                <div className="col-6">
                  <MDBInput
                    type="number"
                    label="Duración (seg)"
                    value={duracion}
                    min={3}
                    onChange={(e) => setDuracion(Number(e.target.value))}
                  />
                </div>
              </div>

              {/* Medios */}
              <div className="exhi-section-editor-medios">
                <div className="exhi-section-editor-medios-title">Medio visual</div>
                {medioVisual ? (
                  <div className="exhi-section-editor-medio-row">
                    {medioVisual.thumbUrl && (
                      <img src={medioVisual.thumbUrl} alt="" style={{ width: 40, height: 30, objectFit: "cover", borderRadius: 3 }} />
                    )}
                    <span className="exhi-section-editor-medio-name">
                      {medioVisual.tituloMfm || medioVisual.mediaType}
                    </span>
                    {medioVisual.idSeccionMedio && (
                      <MDBBtn size="sm" color="link" onClick={() => setTrimTarget(medioVisual)}>
                        Trim
                      </MDBBtn>
                    )}
                    <MDBBtn size="sm" color="danger" outline onClick={() => {
                      if (medioVisual.idSeccionMedio) handleRemoveMedio(medioVisual.idSeccionMedio);
                      else setMedioVisual(null);
                    }}>
                      ✕
                    </MDBBtn>
                  </div>
                ) : (
                  <MDBBtn size="sm" color="secondary" onClick={() => { setPickerMode("visual"); setPickerOpen(true); }}>
                    Seleccionar visual
                  </MDBBtn>
                )}

                <div className="exhi-section-editor-medios-title mt-3">Pistas de audio</div>
                {mediosAudio.map((a, i) => (
                  <div key={i} className="exhi-section-editor-medio-row">
                    <span>🎵</span>
                    <span className="exhi-section-editor-medio-name">{a.tituloMfm || `Pista ${i + 1}`}</span>
                    {a.idSeccionMedio && (
                      <MDBBtn size="sm" color="link" onClick={() => setTrimTarget(a)}>
                        Trim
                      </MDBBtn>
                    )}
                    <MDBBtn size="sm" color="danger" outline onClick={() => {
                      if (a.idSeccionMedio) handleRemoveMedio(a.idSeccionMedio);
                      else setMediosAudio((prev) => prev.filter((_, j) => j !== i));
                    }}>
                      ✕
                    </MDBBtn>
                  </div>
                ))}
                <div className="exhi-section-editor-add-row">
                  <MDBBtn size="sm" color="secondary" onClick={() => { setPickerMode("audio"); setPickerOpen(true); }}>
                    + Audio
                  </MDBBtn>
                </div>
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              {isEdit && !confirmDelete && (
                <MDBBtn color="danger" outline className="me-auto" onClick={() => setConfirmDelete(true)} disabled={saving}>
                  Eliminar
                </MDBBtn>
              )}
              {isEdit && confirmDelete && (
                <div className="me-auto d-flex align-items-center gap-2">
                  <span className="text-danger small">¿Eliminar esta sección?</span>
                  <MDBBtn size="sm" color="danger" onClick={handleDeleteSection} disabled={saving}>Sí, eliminar</MDBBtn>
                  <MDBBtn size="sm" color="secondary" onClick={() => setConfirmDelete(false)} disabled={saving}>Cancelar</MDBBtn>
                </div>
              )}
              <MDBBtn color="secondary" onClick={handleClose} disabled={saving}>Cancelar</MDBBtn>
              <MDBBtn onClick={handleSave} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <MfmFilePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
        appCode={null}
        mediaType={pickerMode === "audio" ? "audio" : null}
        title={pickerMode === "visual" ? "Seleccionar visual" : "Seleccionar audio"}
      />

      {trimTarget && (
        <MediaTrimmer
          show={!!trimTarget}
          setShow={(v) => { if (!v) setTrimTarget(null); }}
          medio={trimTarget}
          onSaved={() => {
            setTrimTarget(null);
            onSaved && onSaved();
          }}
        />
      )}
    </>
  );
}
